import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, lighten } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { IconButton } from '@material-ui/core';
import { useTranslation } from '../../../src/i18n';
import { TP, urlRegex } from '../../../constants/consts';
import TextField from '../../Fields/TextField';
import Button from '../../Button';
import SubmitModalLayout from '../../SubmitModalLayout';
import { colors } from '../../../constants/styleConstants';
import UploadFileButton from '../../UploadFileButton';
import ImageSet from '../../ImageSet';
import AddFieldWithButton from '../AddFieldWithButton';
import useImageValidate from '../../../utils/hooks/useImageValidate';
import { getAcceptFormatsTypesForFiles, joinMediaFormats } from '../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      alignItems: 'center',
    },
  },
  inputField: {
    flex: 1,
  },
  orBlock: {
    textTransform: 'uppercase',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  imageBlock: {
    padding: '0 16px 24px',
    display: 'flex',
  },
  imageWrapper: {
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.2)',
    marginRight: theme.spacing(2),
    position: 'relative',
  },
  image: {
    maxHeight: 200,
    width: 'auto',
    margin: 'auto',
    display: 'block',
  },
  closeIcon: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    cursor: 'pointer',
    backgroundColor: lighten(colors.white, 0.8),
    padding: theme.spacing(1),
  },
  errorMessage: {
    position: 'absolute',
    bottom: '-20px',
  },
  imageInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: 1,
  },
  inputFieldBlock: {
    width: '100%',
  },
  uploadButton: {
    marginRight: 'auto',
    marginLeft: 'auto',
  },
}));

const AddImageModal = ({
  isOpened,
  onClose,
  imageUrl,
  uploadPhoto,
  fileUpload,
  urlUpload,
  withTitle,
  title,
  isLoading,
  formats,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const validateImage = useImageValidate();

  const [webLinkInputValue, updateWebLinkInputValue] = React.useState('');
  const [imageError, setImageError] = React.useState(false);
  const [imageFile, setImageFile] = React.useState(imageUrl ? { type: 'url', src: imageUrl } : null);
  const [imageTitle, setImageTitle] = React.useState('');

  const uploadUrlPhoto = () => {
    uploadPhoto({
      type: imageFile?.type,
      src: imageFile?.type === 'blob' ? imageFile?.blob?.result : imageFile?.src,
      name: imageFile?.type === 'blob' ? imageFile?.path : '',
      title: imageTitle,
      file: imageFile.file,
    });
  };

  const setImageSrc = (type, file) => {
    setImageError(false);
    setImageFile({ type, ...file, file: file?.file });
    if (type === 'blob') {
      updateWebLinkInputValue('');
    }
  };

  const clearPhoto = () => {
    setImageError(false);
    setImageFile(null);
    updateWebLinkInputValue('');
  };

  const setImageTitleValue = e => {
    setImageTitle(e?.target?.value);
  };

  const setImageUrlValue = value => {
    updateWebLinkInputValue(value);
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      onClose={onClose}
      title1={t(title)}
      title2={t(`${TP}.FN_ACCEPTING_FORMATS`, { formats: joinMediaFormats(formats) })}
      disableBackdropClick
      inTop={false}
      withoutFooter
    >
      <div className={classes.root}>
        {urlUpload && (
          <AddFieldWithButton
            id="image-link"
            value={webLinkInputValue}
            onChange={setImageUrlValue}
            onAdd={url => validateImage(url, value => setImageSrc('url', { src: value }))}
            className={classes.inputField}
            validateRegex={urlRegex}
          />
        )}
        {fileUpload && urlUpload && <div className={classes.orBlock}>{t(`${TP}.FN_OR`)}</div>}
        {fileUpload && (
          <UploadFileButton
            accept={getAcceptFormatsTypesForFiles('image', formats)}
            onFilesAdd={files => setImageSrc('blob', files[0])}
            className={classes.uploadButton}
          />
        )}
      </div>
      {imageFile && (
        <div className={classes.imageBlock}>
          <div className={classes.imageWrapper}>
            <ImageSet
              src={imageFile?.src}
              alt={imageFile?.name || 'photo'}
              className={classes.image}
              onError={state => setImageError(state)}
            />
            <IconButton className={classes.closeIcon} onClick={clearPhoto}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.imageInfoBlock}>
            <div className={classes.inputFieldBlock}>
              {withTitle && (
                <TextField
                  id="image_title"
                  value={imageTitle}
                  label={t(`${TP}.FN_IMAGE_TITLE`)}
                  onChange={setImageTitleValue}
                />
              )}
            </div>
            <Button disabled={imageError} onClick={uploadUrlPhoto} isLoading={isLoading}>
              {t(`${TP}.m_SAVE`)}
            </Button>
          </div>
        </div>
      )}
    </SubmitModalLayout>
  );
};

AddImageModal.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageUrl: PropTypes.string,
  uploadPhoto: PropTypes.func.isRequired,
  fileUpload: PropTypes.bool,
  urlUpload: PropTypes.bool,
  withTitle: PropTypes.bool,
  isLoading: PropTypes.bool,
  formats: PropTypes.array,
};

AddImageModal.defaultProps = {
  imageUrl: '',
  fileUpload: true,
  urlUpload: true,
  withTitle: true,
  isLoading: false,
  formats: [],
};

export default memo(AddImageModal);
